import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function isInvoiceInSelectedFys(inv: any, selectedFys: string[]): boolean {
  if (!selectedFys || selectedFys.length === 0) return true;
  if (inv.financial_year) {
    return selectedFys.includes(inv.financial_year);
  }
  const d = new Date(inv.Date);
  if (isNaN(d.getTime())) return false;
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed: 0 = Jan
  let fy = "";
  if (month >= 3) { // Apr or later
    fy = `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
  } else {
    fy = `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
  }
  return selectedFys.includes(fy);
}

interface TdsRecord {
  id: number;
  Date: string;
  InvoiceNo: string;
  company_name: string;
  vendor_name: string;
  pan_number: string;
  Base_Amount: number;
  TDSAmount: number;
  TDS_Rate: number;
}

const TdsReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<TdsRecord[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(() => {
    const s = localStorage.getItem("selected_company_id");
    return s && s !== "all" ? s : "";
  });
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    const activeWorkspaceId = localStorage.getItem("selected_company_id");
    // Load companies list
    axios.get(`${API_URL}/auth/companies`)
      .then(res => {
        const compList = res.data || [];
        if (activeWorkspaceId && activeWorkspaceId !== "all") {
          setCompanies(compList.filter((c: any) => String(c.id) === String(activeWorkspaceId)));
          setSelectedCompanyId(String(activeWorkspaceId));
        } else {
          setCompanies(compList);
        }
      })
      .catch(() => setErrMsg("Failed to load companies"));
  }, []);

  const convertToISODate = (dmy: string): string => {
    if (!dmy) return new Date().toISOString().split('T')[0];
    const parts = dmy.split(/[-/.]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return dmy;
      }
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) year = '20' + year;
      return `${year}-${month}-${day}`;
    }
    return dmy;
  };

  const fetchTdsData = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const [purchasesRes, vendorsRes, settingRes, statementRowsRes] = await Promise.all([
        axios.get(`${API_URL}/auth/purchase-invoices`),
        axios.get(`${API_URL}/auth/vendors`),
        axios.get(`${API_URL}/auth/setting`),
        axios.get(`${API_URL}/auth/bank-statement-rows`)
      ]);

      let purchases = purchasesRes.data || [];
      const vendors = vendorsRes.data || [];
      const settingData = settingRes.data || {};
      let statementRows = statementRowsRes.data || [];

      let selectedFys: string[] = [];
      try {
        if (settingData.selected_fys) {
          selectedFys = JSON.parse(settingData.selected_fys);
        }
      } catch (e) {}

      let directExpenseTdsRows = statementRows.filter((r: any) => r.mapped_expense_head_id && Number(r.tds_amount) > 0);

      if (selectedFys.length > 0) {
        purchases = purchases.filter((p: any) => isInvoiceInSelectedFys(p, selectedFys));
        directExpenseTdsRows = directExpenseTdsRows.filter((r: any) => {
          const isoDate = convertToISODate(r.date_str);
          return isInvoiceInSelectedFys({ Date: isoDate }, selectedFys);
        });
      }

      // Filter and map TDS records from purchase bills
      const records: TdsRecord[] = purchases
        .filter((p: any) => {
          // Filters
          if (selectedCompanyId && String(p.Company_id) !== selectedCompanyId) return false;
          if (startDate && new Date(p.Date) < new Date(startDate)) return false;
          if (endDate && new Date(p.Date) > new Date(endDate)) return false;
          return p.TDSAmount > 0;
        })
        .map((p: any) => {
          const vendor = vendors.find((v: any) => String(v.id) === String(p.Vendor_id));
          const comp = companies.find((c: any) => String(c.id) === String(p.Company_id));
          
          // Reconstruct base amount: base = total - sgst - cgst - igst
          const baseAmt = Number(p.Total_Amount) - Number(p.SGSTAmount || 0) - Number(p.CGSTAmount || 0) - Number(p.IGSTAmount || 0);
          const tdsRate = baseAmt > 0 ? (Number(p.TDSAmount) * 100) / baseAmt : 0;

          return {
            id: p.id,
            Date: p.Date,
            InvoiceNo: p.InvoiceNo,
            company_name: comp ? comp.company_name : "Unknown",
            vendor_name: vendor ? vendor.company_name : "Unknown",
            pan_number: vendor ? vendor.pancard || "—" : "—",
            Base_Amount: baseAmt,
            TDSAmount: Number(p.TDSAmount),
            TDS_Rate: Math.round(tdsRate * 10) / 10
          };
        });

      // Filter and map TDS records from direct expenses
      const directRecords: TdsRecord[] = directExpenseTdsRows
        .filter((r: any) => {
          if (selectedCompanyId && String(r.company_id) !== selectedCompanyId) return false;
          const isoDate = convertToISODate(r.date_str);
          if (startDate && new Date(isoDate) < new Date(startDate)) return false;
          if (endDate && new Date(isoDate) > new Date(endDate)) return false;
          return true;
        })
        .map((r: any) => {
          const vendor = vendors.find((v: any) => String(v.id) === String(r.mapped_vendor_id));
          const comp = companies.find((c: any) => String(c.id) === String(r.company_id));
          
          const baseAmt = Number(r.debit || 0) - Number(r.gst_amount || 0);
          const tdsRate = baseAmt > 0 ? (Number(r.tds_amount) * 100) / baseAmt : 0;

          return {
            id: 2000000 + r.id,
            Date: convertToISODate(r.date_str),
            InvoiceNo: "Direct Exp (" + (r.expense_head_name || 'Expense') + ")",
            company_name: comp ? comp.company_name : "Unknown",
            vendor_name: vendor ? vendor.company_name : "Direct Expense Mapping",
            pan_number: vendor ? vendor.pancard || "—" : "—",
            Base_Amount: baseAmt,
            TDSAmount: Number(r.tds_amount),
            TDS_Rate: Math.round(tdsRate * 10) / 10
          };
        });

      const combinedRecords = [...records, ...directRecords];
      combinedRecords.sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime());

      setReport(combinedRecords);
    } catch (err) {
      console.error(err);
      setErrMsg("Failed to retrieve purchases data for TDS calculation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companies.length > 0) {
      fetchTdsData();
    }
  }, [companies, selectedCompanyId, startDate, endDate]);

  const totalBase = report.reduce((sum, item) => sum + item.Base_Amount, 0);
  const totalTds = report.reduce((sum, item) => sum + item.TDSAmount, 0);

  return (
    <div className="max-w-7xl mx-auto py-10 px-4">
      <div className="mb-8">
        <h2 className="text-3xl font-extrabold text-blue-800">TDS Report</h2>
        <p className="text-gray-600 mt-2">Deducted Tax Source overview compiled from recorded purchase bills.</p>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white border border-blue-200 rounded-2xl p-6 shadow mb-8">
        <div className="grid md:grid-cols-3 gap-6 items-end">
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">Company</label>
            <select
              value={selectedCompanyId}
              onChange={e => setSelectedCompanyId(e.target.value)}
              className="form-select w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            >
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="form-input w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-blue-900 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="form-input w-full border-2 border-blue-100 rounded-xl bg-white p-2"
            />
          </div>
        </div>
      </div>

      {errMsg && <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-xl border border-red-200 text-center shadow font-semibold">{errMsg}</div>}

      {/* Summary Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6 shadow-md">
          <div className="text-sm font-semibold text-blue-800 uppercase tracking-wider">Total Taxable Value</div>
          <div className="text-3xl font-extrabold text-blue-900 mt-2">₹ {totalBase.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-pink-50 border border-red-100 rounded-2xl p-6 shadow-md">
          <div className="text-sm font-semibold text-red-800 uppercase tracking-wider">Total TDS Deducted</div>
          <div className="text-3xl font-extrabold text-red-900 mt-2">₹ {totalTds.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : report.length === 0 ? (
        <div className="text-center py-10 bg-white shadow rounded-2xl border text-gray-500">No TDS records found for selected criteria.</div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-xl rounded-2xl border">
          <table className="min-w-full text-left border-collapse">
            <thead>
              <tr className="bg-blue-50 border-b">
                <th className="p-4 font-bold text-blue-900">Date</th>
                <th className="p-4 font-bold text-blue-900">Invoice No</th>
                <th className="p-4 font-bold text-blue-900">Company</th>
                <th className="p-4 font-bold text-blue-900">Vendor</th>
                <th className="p-4 font-bold text-blue-900">PAN Card</th>
                <th className="p-4 font-bold text-blue-900 text-right">Taxable base (₹)</th>
                <th className="p-4 font-bold text-blue-900 text-right">Rate</th>
                <th className="p-4 font-bold text-blue-900 text-right">TDS (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {report.map(record => (
                <tr key={record.id} className="hover:bg-blue-50/20 transition-colors">
                  <td className="p-4 whitespace-nowrap">{new Date(record.Date).toLocaleDateString("en-GB")}</td>
                  <td className="p-4 font-semibold text-gray-700">{record.InvoiceNo}</td>
                  <td className="p-4">{record.company_name}</td>
                  <td className="p-4 font-medium">{record.vendor_name}</td>
                  <td className="p-4 font-mono text-gray-600">{record.pan_number}</td>
                  <td className="p-4 text-right font-medium">₹ {record.Base_Amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right text-gray-600 font-bold">{record.TDS_Rate}%</td>
                  <td className="p-4 text-right font-bold text-red-600">₹ {record.TDSAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TdsReport;
